import $ from 'jquery';
import _ from 'lodash';
import { createAction, createReducer, createSelector } from '@reduxjs/toolkit';
import Actions from 'Actions';
import { StorytellerState } from 'store/StorytellerReduxState';
import { ReduxPayload } from 'types';
import { assertHasProperties } from 'common/assertions';

export type ReorderHintPosition = {
  storyUid: string;
  dropIndex: number;
};

export interface DropHintState {
  reorderHintPosition: ReorderHintPosition | null;
}

const initialState: DropHintState = {
  reorderHintPosition: null,
};

/* Action Creators */
export const storyDragLeave = createAction<ReduxPayload>(Actions.STORY_DRAG_LEAVE);
export const storyDragOver = createAction<any>(Actions.STORY_DRAG_OVER);
export const storyDrop = createAction<ReduxPayload>(Actions.STORY_DROP);

/* Selectors */
const getDropHintState = (state: StorytellerState) => state.dropHint;
export const getDropHintPosition = createSelector(getDropHintState, (state) => state.reorderHintPosition);
export const isDraggingOverStory = createSelector(
  [getDropHintState, (state: StorytellerState, storyUid: string) => storyUid],
  (dropHintState, storyUid) => dropHintState.reorderHintPosition?.storyUid === storyUid,
);

/* Reducer */
export const dropHintReducer = createReducer(initialState, (builder) => {
  builder
    .addCase(storyDragLeave, (state, action) => {
      const { storyUid } = action.payload;
      if (state.reorderHintPosition?.storyUid === storyUid) {
        state.reorderHintPosition = null;
      }
    })
    .addCase(storyDragOver, (state, action) => {
      state.reorderHintPosition = findDragOverHintPosition(action.payload);
    })
    .addCase(storyDrop, (state, action) => {
      const { storyUid } = action.payload;
      if (state.reorderHintPosition?.storyUid === storyUid) {
        state.reorderHintPosition = null;
      }
    });
});

const findDragOverHintPosition = (payload: {
  storyUid: string;
  pointer: PointerEvent;
  storyElement: HTMLElement | JQuery<HTMLElement>;
  storyExists: boolean;
}): ReorderHintPosition | null => {
  assertHasProperties(payload, 'storyUid', 'pointer', 'storyElement', 'storyExists');

  if (payload.storyExists) {
    let dropIndex;

    const pointerY = payload.pointer.pageY - window.pageYOffset;

    // _.chain allows you to chain lodash calls together, vs. having to nest individual
    // calls.
    const blocksSortedByVerticalPosition = _.chain($(payload.storyElement).find('.block'))
      .map((block) => {
        return block.getBoundingClientRect();
      })
      .sortBy('top')
      .value();

    // From the bottom, give me first block where the top extent
    // is above the pointer. This is the block we're over.
    const indexOfBlockUnderPointer = _.findLastIndex(blocksSortedByVerticalPosition, (rect) => {
      return rect.top <= pointerY;
    });

    if (indexOfBlockUnderPointer >= 0) {
      const blockExtents = blocksSortedByVerticalPosition[indexOfBlockUnderPointer];
      const height = blockExtents.bottom - blockExtents.top;
      const isOverBottomHalf = pointerY >= blockExtents.top + height / 2;

      dropIndex = indexOfBlockUnderPointer + (isOverBottomHalf ? 1 : 0);
    } else {
      dropIndex = 0;
    }

    return { storyUid: payload.storyUid, dropIndex };
  }
  return null;
};
